"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useI18n } from "@/lib/i18n";

export type ConfirmOptions = {
  title: string;
  description?: string;
  /** Defaults to "Confirm"; pass the verb the button performs where one exists. */
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
};

export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(() =>
  Promise.reject(new Error("useConfirm() needs a ConfirmProvider above it")),
);

/**
 * The app's replacement for window.confirm: one AlertDialog mounted at the
 * root, and a function any component or hook can await. Call sites keep the
 * shape they had ("if (!(await confirm(...))) return") and the answer arrives
 * before anything is asked of the API, exactly as the native prompt did.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ConfirmOptions | null>(null);
  const settle = useRef<(answer: boolean) => void>(() => undefined);
  const confirm = useCallback<ConfirmFn>((options) => {
    // A second question while one is open answers the first with "no" rather
    // than stacking dialogs the reader never saw.
    settle.current(false);
    return new Promise<boolean>((resolve) => {
      settle.current = resolve;
      setRequest(options);
    });
  }, []);
  const answer = (value: boolean) => {
    settle.current(value);
    settle.current = () => undefined;
    setRequest(null);
  };
  const value = useMemo(() => confirm, [confirm]);
  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmDialog request={request} onAnswer={answer} />
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  return useContext(ConfirmContext);
}

function ConfirmDialog({
  request,
  onAnswer,
}: {
  request: ConfirmOptions | null;
  onAnswer: (value: boolean) => void;
}) {
  const { t } = useI18n();
  return (
    <AlertDialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) onAnswer(false);
      }}
    >
      {request ? (
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{request.title}</AlertDialogTitle>
            {request.description ? (
              <AlertDialogDescription>
                {request.description}
              </AlertDialogDescription>
            ) : null}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => onAnswer(false)}>
              {request.cancelLabel ?? t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              destructive={request.destructive}
              onClick={() => onAnswer(true)}
            >
              {request.confirmLabel ?? t("common.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      ) : null}
    </AlertDialog>
  );
}
